import { computed, readonly, ref, watch } from 'vue';
import { defineStore } from 'pinia';
import { useI18n } from 'vue-i18n';
import { useToast } from 'primevue/usetoast';
import { getCategoryNameForXPath, getCategoryXpath, parseModuleXPath } from '../Common/ConfigXPaths';
import type { ConfigModule, ModuleCategory } from '../types/config';
import { createCategory, createElement, createModule } from '../Common/ConfigFactories';
import type { ConfigElement } from '../types/config';
import {
  filterConfigValues,
  filterModulesByFields,
  getCategoryNamesFromElements,
  getDifferenceToConfigFileStructure,
  getElementsValuesNames,
  groupElementsByHeadline,
  sortConfigData,
} from '../Common/ConfigHelpers';
import type { LocalizedConfigBlockDto, LocalizedConfigProfileStructureDto, LocalizedElementDto } from '../types/graphql';
import { useConfigStore } from './config';

export const useConfigParserStore = defineStore('configParser', () => {
  const { t, locale } = useI18n({ useScope: 'global' });
  const toast = useToast();
  const configStore = useConfigStore();

  const selectedConfigModule = ref<number>(0);

  // Centralized config values state
  const configValues = ref<Record<string, string>>({});

  const debouncedConfigValues = ref<Record<string, string>>({});
  let configValuesTimeout: NodeJS.Timeout | null = null;

  // Required fields management
  const requiredElementsMap = ref(new Map<number, ConfigElement>());

  // Deleted categories management for tracking removed categories
  const deletedCategoryXPaths = ref(new Set<string>());

  // Hidden tab XPaths for O(1) lookups for hidden tabs in the UI
  const hiddenTabXPathsSet = ref(new Set<string>());

  // Element reference cache for O(1) lookups
  const elementReferenceCache = ref(new Map<string, ConfigElement>());

  const profileBlocks = ref<LocalizedConfigProfileStructureDto[] | null>(null);
  const useProfileSource = ref(false);


  const organizedProfileData = computed<Record<number, ConfigModule>>(() => {
    if (!profileBlocks.value?.length) return {};
    return organizeProfileDataFromStructure(profileBlocks.value); // use profile DTO
  });


  // Computed properties
  const organizedConfigData = computed(() => {
    if (useProfileSource.value && profileBlocks.value) return organizedProfileData.value;
    if (configStore.configFileStructure) {
      try {
        return organizeConfigDataFromStructure(configStore.configFileStructure);
      } catch (e) {
        console.error('Error organizing configuration elements:', e);
        toast.add({
          severity: 'error',
          summary: t('common.toast.error'),
          detail: t('common.toast.errors.loading'),
          life: 3000
        });
        return {};
      }
    } else {
      return {};
    }
  });

  const sortedModules = computed<ConfigModule[]>(() => {
    return sortConfigData(Object.values(organizedConfigData.value));
  });

  // Required fields computed property
  const requiredFields = computed(() => {
    return getElementsValuesNames(Array.from(requiredElementsMap.value.values()));
  });

  // Computed property for available tabs
  const availableTabs = computed(() => {
    if (!selectedConfigModule.value) return [];

    // Generate tabs from module data
    const moduleData = getModule(selectedConfigModule.value);
    if (!moduleData) return [];

    return Object.values(moduleData.categories).map(tab => ({
      ...tab,
      hidden: deletedCategoryXPaths.value.has(tab.xpath || '')
    }));
  });

  // Watch for changes in configData to reset the selected module if it no longer exists
  watch([
    () => sortedModules.value,
    () => selectedConfigModule.value
  ], ([currentModules, currentSelectedModule]) => {

    if (currentModules.length > 0) {
      // Check if the currently selected module exists in the available modules
      const moduleExists = currentSelectedModule &&
        currentModules.some(module => module.moduleId === currentSelectedModule);

      // If the current selected module does not exist, reset to the first available module
      if (!currentSelectedModule || !moduleExists) {
        setSelectedModule(currentModules[0].moduleId);
      }
    }
  }, { deep: true });

  // Watch for configValues changes and debounce updates
  watch(configValues, (newValues) => {
    if (configValuesTimeout) {
      clearTimeout(configValuesTimeout);
    }

    configValuesTimeout = setTimeout(() => {
      debouncedConfigValues.value = { ...newValues };
    }, 100); // 100ms debounce
  }, { deep: true });

  // Reset updatedValues if configData changes
  watch(
    () => configStore.configFileStructure,
    (newStructure) => {
      if (!useProfileSource.value && newStructure?.length) {
        initializeConfigValues();
      }
    },
    { immediate: true, deep: true }
  );

  // Watch for changes in availableTabs to update required fields
  watch(
    [availableTabs, configValues],
    ([newTabs, currentValues]) => {
      if (!newTabs || !currentValues) return;

      // Update hidden tab XPaths set for O(1) lookups
      const newHiddenXPaths = new Set(
        newTabs
          .filter(tab => tab.hidden && tab.xpath)
          .map(tab => tab.xpath!)
      );
      hiddenTabXPathsSet.value = newHiddenXPaths;

      // Remove hidden tab values from store with O(1) check
      const updatedValues: Record<string, string> = {};
      Object.keys(currentValues).forEach(key => {
        const isHidden = Array.from(newHiddenXPaths).some(xpath => key.startsWith(xpath));
        if (!isHidden) {
          updatedValues[key] = currentValues[key];
        }
      });

      // Update store if values changed
      if (Object.keys(updatedValues).length !== Object.keys(currentValues).length) {
        resetConfigValues();
        updateConfigValues(updatedValues);
      }

      // Filter relevant tabs that are not hidden and have a xpath
      const relevantTabs = newTabs.filter(tab =>
        !tab.hidden &&
        tab.xpath &&
        Object.keys(currentValues).some(key => key.startsWith(tab.xpath!))
      );

      // Collect all required fields from the relevant tabs
      const requiredFields = relevantTabs.flatMap(tab =>
        tab.headlineGroups.flatMap(group =>
          group.elements.filter(el => el.isRequired)
        )
      );
      addRequiredElements(requiredFields);
    },
    { deep: true, immediate: true }
  );

  watch(() => configStore.getConfigFileStructure, () => {
    if (!useProfileSource.value) { configValues.value = {}; }
  });


  // Watch for organized data changes to rebuild element cache
  watch(() => organizedConfigData.value, () => {
    rebuildElementCache();
  }, { deep: true });

  // Initialize element cache when data is available
  watch(() => organizedConfigData.value, (newData) => {
    if (Object.keys(newData).length > 0) {
      rebuildElementCache();
    }
  }, { immediate: true });

  function getAllConfigElements() {
    // Flatten all elements from all modules and categories
    return Object.values(organizedConfigData.value)
      .flatMap(module =>
        Object.values(module.categories)
          .flatMap(category =>
            category.headlineGroups.flatMap(group => group.elements)
          )
      );
  }

  /**
   * Retrieves the current configuration values.
   * @return A record containing the current configuration values.
   */
  function getConfigValues(): Record<string, string> {
    return { ...configValues.value };
  }

  /**
   * Retrieves the debounced configuration values, used for change detection.
   * @return A record containing the debounced configuration values.
   */
  function getDebouncedConfigValues(): Record<string, string> {
    return { ...debouncedConfigValues.value };
  }

  /**
   * Initializes the config values from the current configuration structure.
   */
  function initializeConfigValues() {
    configValues.value = extractConfigValues();
    debouncedConfigValues.value = { ...configValues.value };
  }

  // Actions

  /**
   * Updates a single configuration value.
   * @param elementPath - The path of the element to update.
   * @param value - The new value for the configuration element.
   */
  function updateConfigValue(elementPath: string, value: string) {
    if (!elementPath || elementPath.trim() === '') return;
    configValues.value[elementPath] = value;
  }

  /**
   * Updates multiple configuration values.
   * @param values - An object containing the new values for the configuration elements.
   */
  function updateConfigValues(values: Record<string, string>) {
    Object.assign(configValues.value, values);
  }

  /**
   * Resets the configuration values to an empty state.
   */
  function resetConfigValues() {
    configValues.value = {};
    debouncedConfigValues.value = {};
  }

  /**
   * Rebuilds the element reference cache for O(1) lookups
   */
  function rebuildElementCache() {
    elementReferenceCache.value.clear();

    Object.values(organizedConfigData.value).forEach(module => {
      Object.values(module.categories).forEach(category => {
        category.headlineGroups.forEach(group => {
          group.elements.forEach(element => {
            if (element.elementPath) {
              elementReferenceCache.value.set(element.elementPath, element);
            }
          });
        });
      });
    });
  }

  /**
   * Updates element value with O(1) performance
   * @param elementPath - The path of the element to update
   * @param value - The new value
   */
  function updateElementValue(elementPath: string, value: string) {
    if (!elementPath || elementPath.trim() === '') return;

    // O(1) update operation in configValues
    configValues.value[elementPath] = value;

    // O(1) lookup and update in organized data using cache
    const elementRef = elementReferenceCache.value.get(elementPath);
    if (elementRef) {
      elementRef.elementValue = value;

    } else {
      // Fallback: rebuild cache and try again
      rebuildElementCache();
      const retryElementRef = elementReferenceCache.value.get(elementPath);
      if (retryElementRef) {
        retryElementRef.elementValue = value;
      }
    }
  }

  /**
   * Enhanced undo changes function with O(1) operations
   * @param values - An object containing the element path and the original value to restore.
   */
  const undoChanges = (values: Record<string, string>) => {
    try {
      for (const [elementPath, originalValue] of Object.entries(values)) {
        updateElementValue(elementPath, originalValue);
      }

      toast.add({
        severity: 'success',
        summary: t('common.toast.success'),
        detail: t('config.toast.undoChanges'),
        life: 3000
      });

    } catch (error) {
      console.error('Error undoing changes:', error);
      toast.add({
        severity: 'error',
        summary: t('common.toast.error'),
        detail: t('config.toast.undoChangesError'),
        life: 3000
      });
    }
  };


  /**
   * Wrapper function to compare the current configuration values with the original ones and
   * update only the changed values.
   */
  async function  updateConfigValuesIfChanged() {
    try {
      // Filter out hidden categories from the configValues
      const filteredConfigValues = filterConfigValues(configValues.value, getHiddenCategories());

      if (filteredConfigValues.length > 0 && configStore.configFileStructure) {
        const changes = getDifferenceToConfigFileStructure(configStore.getElementLookupMap, filteredConfigValues);
        if (changes.length > 0) {
          await configStore.updateConfigValues(changes, locale.value);
        }
      }
    } catch (error) {
      console.error('Error updating configuration values:', error);
      throw error;
    }
  }

  /**
   * Retrieves all hidden categories from the organized configuration data.
   * @return An array of ModuleCategory objects that are hidden.
   */
  function getHiddenCategories(): ModuleCategory[] {
    const hiddenCategories: ModuleCategory[] = [];

    Object.values(organizedConfigData.value).forEach(module => {
      Object.values(module.categories).forEach(category => {
        if (category.hidden) {
          hiddenCategories.push(category);
        }
      });
    });
    return hiddenCategories;
  }

  /**
   * Check if a tab XPath is hidden
   * @param xpath - The XPath to check
   * @return True if hidden, false otherwise
   */
  function isTabHidden(xpath: string): boolean {
    return Array.from(hiddenTabXPathsSet.value).some(hiddenXpath => xpath.startsWith(hiddenXpath));
  }

  /**
   * Extracts configuration values from the configFileStructure.
   * @return A record containing the element paths as keys and their values as strings.
   */
  function extractConfigValues(): Record<string, string> {
    const values: Record<string, string> = {};

    if (configStore.configFileStructure) {
      for (const block of configStore.configFileStructure) {
        if (!block.elements) continue;

        for (const el of block.elements) {
          if (el.elementPath) {
            values[el.elementPath] = el.elementValue ?? '';
          }
        }
      }
    }
    return values;
  }


  /**
   * Organizes configuration data from the structure into a record of ConfigModule objects.
   * @param configBlocks - An array of LocalizedConfigBlockDto objects representing the configuration blocks.
   * @return A record where keys are module IDs and values are ConfigModule objects containing organized configuration data.
   */
  function organizeConfigDataFromStructure(configBlocks: LocalizedConfigBlockDto[]): Record<number, ConfigModule> {
    const modules: Record<number, ConfigModule> = {};
    configBlocks.forEach(block => {
      if (!block.elements?.length || !block.elements[0].elementPath) return;

      const moduleXpath = parseModuleXPath(block.elements[0].elementPath);
      if (!modules[block.id]) modules[block.id] = createModule(block, moduleXpath);

      const elementsByCategory: Record<string, ConfigElement[]> = {};
      const categoryNames = getCategoryNamesFromElements(block);

      block.elements.forEach(el => {
        if (!el.elementPath) return;
        const categoryXpath = getCategoryXpath(el.elementPath);
        const categoryName = getCategoryNameForXPath(
          el.elementPath,
          categoryNames[categoryXpath] !== undefined,
          categoryNames[categoryXpath]
        );

        if (!modules[block.id].categories[categoryName]) {
          modules[block.id].categories[categoryName] = createCategory(categoryName, categoryXpath, !!el.isDeletable, [], t);
        }
        (elementsByCategory[categoryXpath] ??= []).push(createElement(el, t));
      });

      Object.values(modules[block.id].categories).forEach(cat => {
        if (cat.xpath && elementsByCategory[cat.xpath]) {
          cat.headlineGroups = groupElementsByHeadline(elementsByCategory[cat.xpath], t);
        }
      });
    });
    return modules;
  }

  function organizeProfileDataFromStructure(blocks: LocalizedConfigProfileStructureDto[]): Record<number, ConfigModule> {
    const modules: Record<number, ConfigModule> = {};

    const synthesize = (block: LocalizedConfigProfileStructureDto) => {
      const modId = block?.subnodeName;
      const els: LocalizedElementDto[] = [];
      if (!modId) return els;
      for (const [path, val] of Object.entries(configValues.value)) {
        if (path.includes(`modul[@id="${modId}"]`)) {
          els.push({ elementPath: path, elementValue: String(val) } as any);
        }
      }
      return els;
    };

    blocks.forEach(block => {
      const sourceEls = block.elements?.length ? block.elements : synthesize(block);

      if (!sourceEls.length || !sourceEls[0].elementPath) {
        return;
      }

      const moduleXpath = parseModuleXPath(sourceEls[0].elementPath!);
      if (!modules[block.id]) modules[block.id] = createModule(block as any, moduleXpath);

      const elementsByCategory: Record<string, ConfigElement[]> = {};
      const categoryNames = getCategoryNamesFromElements(block);

      sourceEls.forEach(el => {
        if (!el.elementPath) return;
        const categoryXpath = getCategoryXpath(el.elementPath);
        const categoryName  = getCategoryNameForXPath(
          el.elementPath,
          categoryNames[categoryXpath] !== undefined,
          categoryNames[categoryXpath]
          ); // 👈 just this
        if (!modules[block.id].categories[categoryName]) {
          modules[block.id].categories[categoryName] = createCategory(categoryName, categoryXpath, !!el.isDeletable, [], t);
        }
        (elementsByCategory[categoryXpath] ??= []).push(createElement(el as any, t));
      });
      Object.values(modules[block.id].categories).forEach(cat => {
        if (cat.xpath && elementsByCategory[cat.xpath]) {
          cat.headlineGroups = groupElementsByHeadline(elementsByCategory[cat.xpath], t);
        }
      });
    });
    return modules;
  }





  /**
   * Set the currently selected configuration module.
   * @param moduleId - The ID of the module to select.
   */
  const setSelectedModule = (moduleId: number) => {
    selectedConfigModule.value = moduleId;
  };

  /**
   * Get Module by ID.
   * @param moduleId - The ID of the module to retrieve.
   * @return The ConfigModule object corresponding to the given ID, or null if not found.
   */
  const getModule = (moduleId: number): ConfigModule => {
    return organizedConfigData.value[moduleId] || null;
  };

  /**
   * Get configuration elements by category name within a specific module.
   * @param moduleId - The ID of the module to retrieve elements from.
   * @param categoryName - The name of the category to retrieve elements from.
   * @return An array of elements in the specified category, or an empty array if the module or category does not exist.
   */
  const getElementsByCategory = (moduleId: number, categoryName: string) => {
    if (!organizedConfigData.value[moduleId]) return [];
    if (!organizedConfigData.value[moduleId].categories[categoryName]) return [];

    // Get the category from the module
    const category = organizedConfigData.value[moduleId].categories[categoryName];

    // Flatten the elements from all headline groups into a single array
    return category.headlineGroups.flatMap(group => group.elements);
  };

  /**
   * Get configuration fields by module.
   * @param fields - An array of field paths to search for.
   * @param modules - Optional array of modules to search in. If not provided, all organized modules will be searched.
   * @return An array of ConfigModule objects that contain the specified fields.
   */
  const getFieldsByModule = (fields: string[], modules?: ConfigModule[]) => {
    const modulesToSearch = modules || Object.values(organizedConfigData.value);
    return filterModulesByFields(fields, modulesToSearch);
  };

  /**
   * Adds required elements to the map for form validation.
   * @param fields - The configuration elements to add as required fields.
   */
  function addRequiredElements(fields: ConfigElement[]) {
    fields.forEach(field => {
      requiredElementsMap.value.set(field.id, field);
    });
  }

  /**
   * Removes required elements from the map.
   * @param fields - The configuration elements to remove from the required fields.
   */
  function removeRequiredElements(fields: ConfigElement[]) {
    fields.forEach(field => {
      requiredElementsMap.value.delete(field.id);
    });
  }

  /**
   * Clears all required elements from the map.
   */
  function clearRequiredElements() {
    requiredElementsMap.value.clear();
  }

  /**
   * Marks a category as deleted by its ModuleCategory object.
   * @param category - The ModuleCategory object to mark as deleted.
   */
  function markCategoryAsDeleted(category: ModuleCategory) {
    if (category && category.xpath) {
      deletedCategoryXPaths.value.add(category.xpath);
    }

    // Collect all required fields from the deleted category
    const removedRequiredFields = category.headlineGroups.flatMap(group =>
      group.elements.filter(el => el.isRequired)
    );
    // Remove the required fields
    removeRequiredElements(removedRequiredFields);
  }

  /**
   * Restores a deleted category by its ModuleCategory object.
   * @param category - The ModuleCategory object to restore.
   */
  function restoreCategory(category: ModuleCategory) {
    if (category && category.xpath) {
      deletedCategoryXPaths.value.delete(category.xpath);

      // Re-add required fields
      const requiredFields = category.headlineGroups.flatMap(group =>
        group.elements.filter(el => el.isRequired)
      );
      addRequiredElements(requiredFields);

      // Re-add config values for the restored category
      category.headlineGroups.forEach(group => {
        group.elements.forEach(element => {
          if (element.elementPath && element.elementValue !== undefined) {
            updateElementValue(element.elementPath, element.elementValue);
          }
        });
      });
    }
  }

  /**
   * Clear all deleted category XPaths
   */
  function clearDeletedCategories() {
    deletedCategoryXPaths.value.clear();
  }

  function setProfileBlocks(blocks: LocalizedConfigProfileStructureDto[]) {
    profileBlocks.value = blocks;
    useProfileSource.value = true;

    const vals: Record<string, string> = {};
    for (const b of blocks) {
      for (const el of b.elements ?? []) {
        if (el.elementPath) vals[el.elementPath] = el.elementValue ?? '';
      }
    }

    resetConfigValues();
    updateConfigValues(vals);
  }


  function exitProfileMode(restoreEditorValues = true) {
    useProfileSource.value = false;
    profileBlocks.value = null;
    if (restoreEditorValues && configStore.configFileStructure?.length) {
      resetConfigValues();
      updateConfigValues(extractConfigValues());
    }
  }

  // Return computed properties and actions
  return {
    updateConfigValuesIfChanged: updateConfigValuesIfChanged,
    undoChanges: undoChanges,

    getSortedModules: sortedModules,
    getSelectedModule: readonly(selectedConfigModule),
    getOrganizedConfigData: readonly(organizedConfigData),
    getConfigValues: readonly(configValues),
    getDebouncedConfigValues: readonly(debouncedConfigValues),
    getRequiredFields: requiredFields,
    getAvailableTabs: readonly(availableTabs), // Als computed property statt Funktion
    isTabHidden: isTabHidden,


    // Functions
    setSelectedModule: setSelectedModule,
    getModule: getModule,
    getElementsByCategory: getElementsByCategory,
    getFieldsByModule: getFieldsByModule,
    extractConfigValues: extractConfigValues,
    getHiddenCategories: getHiddenCategories,
    getConfigValuesAsObject: getConfigValues,
    getDebouncedConfigValuesAsObject: getDebouncedConfigValues,

    // Config values management
    initializeConfigValues: initializeConfigValues,
    updateConfigValue: updateConfigValue,
    updateConfigValues: updateConfigValues,
    resetConfigValues: resetConfigValues,

    // Required fields management
    addRequiredElements: addRequiredElements,
    removeRequiredElements: removeRequiredElements,
    clearRequiredElements: clearRequiredElements,

    // Element value update
    updateElementValue: updateElementValue,

    // Category management
    markCategoryAsDeleted: markCategoryAsDeleted,
    restoreCategory: restoreCategory,
    clearDeletedCategories: clearDeletedCategories,

    //configProfiles management
    setProfileBlocks,
    exitProfileMode,
    useProfileSource,
    getOrganizedProfileData: readonly(organizedProfileData),
    getAllConfigElements,
  };
});
